import React from 'react'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Snackbar from '@mui/material/Snackbar'
import TextField from '@mui/material/TextField'

import { Text } from '@mxenabled/mxui'

import { ANALYTICS_EVENTS, Drawer, Loader, useEvent } from '../../../common'
import { type HelpAlertProps, HelpCopy } from '../../../core'
import { RequestSupportHeader } from './RequestSupportHeader'

interface ContactSupportProps {
  alert: HelpAlertProps
  copy: HelpCopy
  email?: string | null | undefined
  institution?: string
  isLoading: boolean
  isOpen: boolean
  onClose: () => void
  onSubmit: (message: string) => void
  setAlert: (message: string) => void
  topic: string
}

export const ContactSupport: React.FC<ContactSupportProps> = ({
  alert,
  copy,
  email,
  institution,
  isLoading,
  isOpen,
  onClose,
  onSubmit,
  setAlert,
  topic,
}) => {
  const [message, setMessage] = React.useState('')

  const { onEvent } = useEvent()

  const handleClose = () => !isLoading && onClose()

  const handlePrimaryAction = () => {
    onEvent(ANALYTICS_EVENTS.HELP_SUBMIT_SUPPORT_REQUEST, {
      supportRequest: message,
    })

    setMessage('')
    onSubmit(message)
  }

  const subtitle = institution
    ? copy.request_support_institution_short_content
    : copy.request_support_contact_short_content
  return (
    <Drawer
      isOpen={isOpen}
      isPrimaryDisabled={!message || !email}
      onClose={handleClose}
      onPrimaryAction={handlePrimaryAction}
      onSecondaryAction={handleClose}
      secondaryColor="primary"
      title={copy.request_support}
    >
      {isLoading ? (
        <Loader />
      ) : (
        <React.Fragment>
          <RequestSupportHeader
            hasIcon={!institution}
            institution={institution}
            reverse={!!institution}
            subtitle={subtitle}
            title={topic}
          />

          <Box sx={{ px: 24 }}>
            <TextField
              error={!email}
              fullWidth={true}
              helperText={!email ? copy.request_support_email_not_exists_message : null}
              label={copy.request_contact_support_label}
              multiline={true}
              onChange={({ target: { value } }) => setMessage(value)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{
                '& .MuiInputBase-root': { alignItems: 'flex-start', minHeight: 124 },
                '& .MuiInputBase-input::placeholder': {
                  fontSize: 13,
                  color: 'text.secondary',
                },
              }}
              value={message}
              variant="outlined"
            />
          </Box>
          <Box sx={{ p: '12px 24px 64px' }}>
            <Text
              color="secondary"
              sx={{
                fontSize: 13,
                fontWeight: 400,
              }}
              variant="subtitle2"
            >
              {copy.request_contact_support_warning}
            </Text>
          </Box>
        </React.Fragment>
      )}
      {alert.message && (
        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          autoHideDuration={3500}
          onClose={() => setAlert('')}
          open={!!alert.message && alert.severity === 'error' && !isLoading}
        >
          <Alert onClose={() => setAlert('')} severity={alert.severity} variant="filled">
            {alert.message}
          </Alert>
        </Snackbar>
      )}
    </Drawer>
  )
}
